/**
 * AP-3 — Main-Thread-Klient für den can-core-Worker.
 * Kapselt Worker-Start, Versionsabgleich, Kommando/Antwort-Zuordnung und
 * die Ereignisverteilung. Siehe CONTRACTS.md §3.
 */

import type { CanBitrate, CanFrameInit, PortRef } from '../can/ICanAdapter';
import { requestPort } from '../can/portFilters';
import type { TraceFormat } from '../trace/types';
import {
  PROTOCOL_VERSION,
  type BridgeTarget,
  type CanBackend,
  type DecodeFlags,
  type DetailWatch,
  type FilterState,
  type SnapshotKind,
  type WorkerCommand,
  type WorkerEvent,
} from '../worker/protocol';

type EventOfType<T extends WorkerEvent['type']> = Extract<WorkerEvent, { type: T }>;
type Listener<T extends WorkerEvent['type']> = (event: EventOfType<T>) => void;

// Ein direktes Omit<WorkerCommand, 'requestId'> wuerde die Union auf die
// gemeinsamen Felder reduzieren; die Verteilung ueber `extends` erhaelt sie.
type CommandPayload = WorkerCommand extends infer C
  ? C extends WorkerCommand
    ? Omit<C, 'requestId'>
    : never
  : never;

interface Pending {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
}

export class WorkerCommandError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'WorkerCommandError';
  }
}

export class CanConnection {
  private readonly worker: Worker;
  private readonly pending = new Map<number, Pending>();
  private readonly listeners = new Map<string, Set<(event: WorkerEvent) => void>>();
  private nextRequestId = 1;
  private readyPromise: Promise<void>;

  constructor() {
    this.worker = new Worker(new URL('../worker/can-core.worker.ts', import.meta.url), {
      type: 'module',
      name: 'can-core',
    });
    this.worker.addEventListener('message', (event: MessageEvent<WorkerEvent>) => {
      this.dispatch(event.data);
    });

    this.readyPromise = new Promise<void>((resolve, reject) => {
      const off = this.on('ready', () => {
        off();
        this.send({ type: 'hello', protocolVersion: PROTOCOL_VERSION }).then(
          () => resolve(),
          (error: unknown) => reject(error instanceof Error ? error : new Error(String(error))),
        );
      });
    });
  }

  /** Aufgelöst, sobald der Worker läuft und die Protokollversion passt. */
  ready(): Promise<void> {
    return this.readyPromise;
  }

  on<T extends WorkerEvent['type']>(type: T, listener: Listener<T>): () => void {
    const set = this.listeners.get(type) ?? new Set();
    set.add(listener as (event: WorkerEvent) => void);
    this.listeners.set(type, set);
    return () => set.delete(listener as (event: WorkerEvent) => void);
  }

  /** Fordert die Freigabe eines Ports an — nur aus einer Nutzergeste heraus. */
  async pickPort(allowAll = false): Promise<PortRef> {
    return requestPort(allowAll);
  }

  /**
   * AP-15: Kandidaten der Brücke (PEAK, PEAK FD, Sontheim/CANfox). Läuft im
   * Worker, weil `can_bridge` nur einen Client zulässt.
   */
  async listBridgePorts(bridge: BridgeTarget): Promise<PortRef[]> {
    const value = await this.send<{ ports: PortRef[] }>({ type: 'listBridgePorts', bridge });
    return value.ports;
  }

  async open(
    port: PortRef,
    bitrate: CanBitrate,
    backend: CanBackend = 'slcan',
    bridge?: BridgeTarget,
  ): Promise<void> {
    await this.send(
      bridge === undefined
        ? { type: 'open', port, bitrate, backend }
        : { type: 'open', port, bitrate, backend, bridge },
    );
  }

  async close(): Promise<void> {
    await this.send({ type: 'close' });
  }

  async sendFrame(frame: CanFrameInit): Promise<void> {
    await this.send({ type: 'send', frame });
  }

  /** AP-10: solange gesetzt, laufen gesendete Frames in den Empfangspfad zurück. */
  async setSimulationMode(enabled: boolean): Promise<void> {
    await this.send({ type: 'setSimulationMode', enabled });
  }

  /** AP-10: Detailfelder für die offenen ISOBUS-/CANopen-Detailfenster abonnieren. */
  async setDetailWatch(watches: DetailWatch[]): Promise<void> {
    await this.send({ type: 'setDetailWatch', watches });
  }

  /** Zyklisches Senden einer Zeile (TX Message / Engine Simulator). */
  async txStart(slotId: number, frame: CanFrameInit, periodMs: number): Promise<void> {
    await this.send({ type: 'txStart', slotId, frame, periodMs });
  }

  async txStop(slotId: number): Promise<void> {
    await this.send({ type: 'txStop', slotId });
  }

  async setCapture(running: boolean): Promise<void> {
    await this.send({ type: 'setCapture', running });
  }

  async setFilter(filter: FilterState): Promise<void> {
    await this.send({ type: 'setFilter', filter });
  }

  /** Menü "Activate ISOBUS/CANopen Decoding" — zwei unabhängige Schalter (FM-MEN-09/10). */
  async setDecodeFlags(flags: DecodeFlags): Promise<void> {
    await this.send({ type: 'setDecodeFlags', flags });
  }

  /** `Load DBC...` (FM-MEN-01). Wirft, solange AP-4 die Dekodierung nicht implementiert. */
  async loadDbc(text: string): Promise<{ messageCount: number }> {
    return this.send({ type: 'loadDbc', text });
  }

  /** `Load EDS...` (FM-MEN-06) — schaltet CANopen implizit ein, sobald AP-4 es umsetzt. */
  async loadEds(text: string): Promise<{ entryCount: number }> {
    return this.send({ type: 'loadEds', text });
  }

  /** `Reset Decoding` (FM-MEN-12). */
  async resetDecoding(): Promise<void> {
    await this.send({ type: 'resetDecoding' });
  }

  async getSnapshot(kind: SnapshotKind, arg?: number): Promise<unknown> {
    return this.send(arg === undefined ? { type: 'getSnapshot', kind } : { type: 'getSnapshot', kind, arg });
  }

  /** AP-11: der Worker schreibt selbst mit — nur er sieht die ungefilterten Frames. */
  async recordStart(handle: FileSystemFileHandle, format: TraceFormat): Promise<void> {
    await this.send({ type: 'recordStart', handle, format });
  }

  async recordStop(): Promise<void> {
    await this.send({ type: 'recordStop' });
  }

  /** `frames` im gepackten Format aus CONTRACTS.md §3.2; wird übertragen, nicht kopiert. */
  async playbackLoad(frames: ArrayBuffer): Promise<{ frameCount: number }> {
    return this.send({ type: 'playbackLoad', frames }, [frames]);
  }

  async playbackStart(loop: boolean): Promise<void> {
    await this.send({ type: 'playbackStart', loop });
  }

  async playbackStop(): Promise<void> {
    await this.send({ type: 'playbackStop' });
  }

  async resetStats(): Promise<void> {
    await this.send({ type: 'resetStats' });
  }

  dispose(): void {
    this.worker.terminate();
    for (const entry of this.pending.values()) {
      entry.reject(new WorkerCommandError('terminated', 'Worker wurde beendet'));
    }
    this.pending.clear();
  }

  private send<T = unknown>(command: CommandPayload, transfer?: Transferable[]): Promise<T> {
    const requestId = this.nextRequestId;
    this.nextRequestId += 1;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(requestId, {
        resolve: resolve as (value: unknown) => void,
        reject,
      });
      const message = { ...command, requestId } as WorkerCommand;
      if (transfer !== undefined && transfer.length > 0) {
        this.worker.postMessage(message, transfer);
      } else {
        this.worker.postMessage(message);
      }
    });
  }

  private dispatch(event: WorkerEvent): void {
    if (event.type === 'result') {
      const entry = this.pending.get(event.requestId);
      this.pending.delete(event.requestId);
      if (entry === undefined) return;
      if (event.ok) entry.resolve(event.value);
      else entry.reject(new WorkerCommandError(event.error.code, event.error.message));
      return;
    }

    const set = this.listeners.get(event.type);
    if (set === undefined) return;
    for (const listener of set) listener(event);
  }
}
